export const PrivacyAgreementContent = () => {
  return (
    <div className="space-y-4 text-sm leading-6">
      <h1 className="text-lg font-bold uppercase">NutriCoach Privacy Policy</h1>
      <p className="text-xs font-semibold">Effective date: January 2026</p>

      <section className="space-y-2">
        <p>
          This Privacy Policy explains how <strong>NUTRICOACH LIMITED</strong>, company number{" "}
          <strong>14709771</strong>, registered office{" "}
          <strong>79 The Mount, Hale Barns, Cheshire, WA15 8SZ, United Kingdom</strong> (
          <strong>"NutriCoach"</strong>, <strong>"we"</strong>, <strong>"us"</strong> or{" "}
          <strong>"our"</strong>), collects, uses, stores, shares and otherwise processes your
          personal data when you use the NutriCoach mobile application, website, content, features
          and related services (together, the <strong>"Platform"</strong>).
        </p>
        <p>
          NutriCoach is a digital, lifestyle-led nutrition and food-planning platform intended for
          general wellness and lifestyle support. It is not a medical service.
        </p>
        <p>
          Please read this Privacy Policy carefully. By using NutriCoach, creating an account,
          starting a trial or purchasing a subscription, you acknowledge that your personal data
          will be handled as described in this Privacy Policy.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">1. Who we are</h2>
        <p>
          NutriCoach is the controller of your personal data for the purposes of UK data protection
          law in relation to the processing described in this Privacy Policy.
        </p>
        <address className="space-y-1 not-italic">
          <p>
            <strong>Controller:</strong> NUTRICOACH LIMITED
          </p>
          <p>
            <strong>Company number:</strong> 14709771
          </p>
          <p>
            <strong>Registered office:</strong> 79 The Mount, Hale Barns, Cheshire, WA15 8SZ, United
            Kingdom
          </p>
          <p>
            <strong>Email:</strong> hello@nutricoach.co.uk
          </p>
        </address>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">2. Scope of this Privacy Policy</h2>
        <p>This Privacy Policy applies to personal data we collect:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>through the NutriCoach app;</li>
          <li>through our website;</li>
          <li>through account creation and onboarding;</li>
          <li>when you subscribe, start a free trial or interact with billing flows;</li>
          <li>when you contact us;</li>
          <li>when you respond to surveys, forms or feedback requests;</li>
          <li>when you use features within the Platform; and</li>
          <li>
            through certain analytics, cookies and similar technologies on our website and app,
            where used.
          </li>
        </ul>
        <p>
          This Policy applies to users globally, although NutriCoach is initially intended primarily
          for UK users.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">3. The personal data we collect</h2>
        <p>We may collect the following categories of personal data.</p>

        <h3 className="font-semibold">3.1 Identity and account data</h3>
        <ul className="list-disc space-y-1 pl-5">
          <li>name</li>
          <li>email address</li>
          <li>password and login credentials</li>
          <li>account ID</li>
          <li>country or region</li>
          <li>app store account-related subscription identifiers where relevant</li>
        </ul>

        <h3 className="font-semibold">3.2 Profile and onboarding data</h3>
        <ul className="list-disc space-y-1 pl-5">
          <li>age or date of birth</li>
          <li>sex or gender where relevant to app functionality</li>
          <li>height</li>
          <li>weight</li>
          <li>target weight or goals</li>
          <li>activity-related information</li>
          <li>food preferences</li>
          <li>dietary preferences</li>
          <li>allergies or intolerances</li>
          <li>meal structure preferences</li>
          <li>lifestyle and routine information you choose to provide</li>
        </ul>

        <h3 className="font-semibold">3.3 Health-related and wellbeing data</h3>
        <p>Depending on what you provide, this may include:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>weight history</li>
          <li>eating-behaviour information</li>
          <li>information about pregnancy or breastfeeding</li>
          <li>medical-condition information</li>
          <li>medication-related information</li>
          <li>mental health-related information relevant to suitability or safe use</li>
          <li>information about symptoms, restrictions or health circumstances you disclose</li>
        </ul>
        <p>
          Some of this data may be <strong>data concerning health</strong> and therefore{" "}
          <strong>special category data</strong> under the UK GDPR.
        </p>

        <h3 className="font-semibold">3.4 Usage and technical data</h3>
        <ul className="list-disc space-y-1 pl-5">
          <li>device information</li>
          <li>operating system</li>
          <li>app version</li>
          <li>IP address</li>
          <li>browser type</li>
          <li>crash logs</li>
          <li>diagnostic information</li>
          <li>app interaction data</li>
          <li>referral data</li>
          <li>website usage data</li>
          <li>cookie and similar technology data where used</li>
        </ul>

        <h3 className="font-semibold">3.5 Subscription and transaction data</h3>
        <ul className="list-disc space-y-1 pl-5">
          <li>subscription status</li>
          <li>trial status</li>
          <li>billing plan</li>
          <li>renewal dates</li>
          <li>app store transaction identifiers</li>
          <li>limited purchase metadata supplied to us by Apple or Google</li>
        </ul>
        <p>
          NutriCoach does <strong>not</strong> intend to store your full card details where
          purchases are made through the Apple App Store or Google Play Store.
        </p>

        <h3 className="font-semibold">3.6 Communications and support data</h3>
        <ul className="list-disc space-y-1 pl-5">
          <li>emails or messages you send us</li>
          <li>feedback</li>
          <li>survey responses</li>
          <li>customer support history</li>
          <li>complaints or dispute information</li>
        </ul>

        <h3 className="font-semibold">3.7 Marketing and preference data</h3>
        <ul className="list-disc space-y-1 pl-5">
          <li>your communication preferences</li>
          <li>whether you opted into marketing</li>
          <li>engagement with marketing emails where lawful and technically supported</li>
        </ul>

        <h3 className="font-semibold">3.8 AI coaching and voice feature data</h3>
        <p>
          When you choose to use AI coaching, photo analysis, or voice features, we may collect and
          process:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>messages and questions you send to the AI coach;</li>
          <li>food, meal, recipe, or other photographs you choose to submit;</li>
          <li>voice recordings you choose to submit for transcription;</li>
          <li>text transcriptions produced from those voice recordings;</li>
          <li>
            meal-plan context and relevant nutrition, profile, preference, goal, and health-related
            information used to personalise a response; and
          </li>
          <li>
            non-identifying technical information needed to operate, secure, and troubleshoot the
            feature.
          </li>
        </ul>
        <p>
          We do not intentionally send direct account identifiers such as your name, email address,
          or NutriCoach user ID to our AI coaching or voice processing providers. However, messages,
          photographs, and voice recordings you choose to submit may contain information that
          identifies you or another person if you include it. Please avoid including another
          person&apos;s information unless you are authorised to provide it.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">4. How we collect your personal data</h2>
        <p>We collect personal data:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            directly from you when you sign up, complete onboarding, subscribe, contact us or use
            the Platform;
          </li>
          <li>
            automatically when you use the Platform, including through app and website analytics and
            similar technologies where applicable;
          </li>
          <li>
            from Apple App Store or Google Play Store in connection with subscription status,
            purchase validation and account administration;
          </li>
          <li>from service providers who help us run the Platform; and</li>
          <li>
            in limited cases, from third parties where you ask us to connect or verify information,
            or where lawful and necessary.
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">5. How we use your personal data</h2>
        <p>We use your personal data to:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>create and manage your account;</li>
          <li>provide the Platform and its features to you;</li>
          <li>personalise your in-app experience based on the information you provide;</li>
          <li>
            generate food planning, meal structure, calorie or nutrition-related outputs within the
            limits described in our Terms;
          </li>
          <li>
            provide AI coaching by processing the messages, photographs, and relevant meal-plan,
            nutrition, and profile context you submit;
          </li>
          <li>transcribe voice recordings into text when you choose to use voice input;</li>
          <li>
            determine whether the Platform may be unsuitable for you based on information you
            provide;
          </li>
          <li>manage subscriptions, free trials, renewals and account administration;</li>
          <li>communicate with you about your account, service updates and support issues;</li>
          <li>respond to enquiries, complaints and data rights requests;</li>
          <li>improve, test, troubleshoot and develop the Platform;</li>
          <li>monitor usage, performance and product quality;</li>
          <li>detect misuse, fraud, abuse, bugs or security incidents;</li>
          <li>comply with legal and regulatory obligations;</li>
          <li>establish, exercise or defend legal claims; and</li>
          <li>send you marketing communications where we are allowed to do so.</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">6. Our lawful bases for processing</h2>
        <p>
          Under UK GDPR, organisations must identify a lawful basis for processing personal data.
        </p>
        <p>Depending on the context, we rely on the following lawful bases:</p>

        <h3 className="font-semibold">6.1 Contract</h3>
        <p>
          We process personal data where necessary to provide the Platform to you, manage your
          account, administer your subscription, and deliver the services you request.
        </p>

        <h3 className="font-semibold">6.2 Legitimate interests</h3>
        <p>
          We process certain personal data where necessary for our legitimate interests, including:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>operating and improving the Platform;</li>
          <li>product analytics and service optimisation;</li>
          <li>preventing fraud and misuse;</li>
          <li>securing our systems;</li>
          <li>handling support and disputes;</li>
          <li>understanding user engagement; and</li>
          <li>developing and improving NutriCoach.</li>
        </ul>
        <p>
          Where we rely on legitimate interests, we consider and balance any potential impact on
          your rights and freedoms.
        </p>

        <h3 className="font-semibold">6.3 Legal obligation</h3>
        <p>
          We may process personal data where necessary to comply with legal obligations, including
          tax, accounting, consumer law, law enforcement requests, court orders and regulatory
          obligations.
        </p>

        <h3 className="font-semibold">6.4 Consent</h3>
        <p>
          Where required, we rely on your consent, including for certain health-related information,
          certain optional profile inputs, sharing personal data with third-party AI and voice
          processing providers, and certain marketing or cookie-related processing. Where consent is
          our basis, you can withdraw it at any time, although this will not affect processing
          already carried out before withdrawal. Withdrawing consent for AI or voice processing may
          prevent you from using features that depend on that processing.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">
          7. Special category data and health-related information
        </h2>
        <p>
          Information concerning physical or mental health is special category data under UK GDPR
          and needs greater protection.
        </p>
        <p>
          We may process health-related or other special category data you provide during onboarding
          or while using the Platform, such as:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>weight and body-related information;</li>
          <li>pregnancy or breastfeeding information;</li>
          <li>allergies or intolerances;</li>
          <li>illness or medical-condition information;</li>
          <li>medication-related information;</li>
          <li>mental health-related information relevant to the suitability of the Platform.</li>
        </ul>
        <p>Where we process special category data, we intend to rely on:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            your <strong>explicit consent</strong>, and/or
          </li>
          <li>
            another lawful condition available under applicable data protection law where relevant
            and appropriate.
          </li>
        </ul>
        <p>
          If you do not provide certain health-related data, some Platform features may not work
          properly or may be unavailable. In some cases, we may also use health-related inputs to
          determine that NutriCoach may be unsuitable for you.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">
          8. Important note on health and suitability data
        </h2>
        <p>
          NutriCoach is designed as a general wellness and lifestyle platform. The fact that we
          collect certain health-related information does <strong>not</strong> mean NutriCoach
          provides medical advice, diagnosis, treatment or clinical care.
        </p>
        <p>We collect some health-related information so that:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            we can tailor the user experience to the extent the Platform is designed to do so;
          </li>
          <li>we can help users determine whether the Platform may be unsuitable for them; and</li>
          <li>we can operate the Platform more responsibly and safely.</li>
        </ul>
        <p>
          Any diet guidance or nutritional framework used within NutriCoach may be overseen
          internally by a Head of Nutrition Standards and Behaviour qualified in Nutrition and
          Weight Management through the Royal Society of Public Health. That does not change the
          non-medical nature of the Platform.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">9. Cookies, analytics and similar technologies</h2>
        <p>If you use our website, we may use cookies and similar technologies to:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>keep the website functioning;</li>
          <li>remember preferences;</li>
          <li>understand website traffic and usage patterns;</li>
          <li>improve performance and user experience; and</li>
          <li>support analytics and marketing, where applicable.</li>
        </ul>
        <p>
          The ICO says privacy notices should explain cookie use and how people can find out more.
        </p>
        <p>
          Where required by law, we will ask for consent before using non-essential cookies or
          similar technologies. You can usually control cookies through your browser settings and,
          where available, our cookie controls.
        </p>
        <p>
          We may use analytics tools and service providers to understand app and website
          performance, user behaviour, crashes and engagement.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">10. Who we share your personal data with</h2>
        <p>
          We may share your personal data with the following categories of recipients where
          necessary:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>cloud hosting and infrastructure providers;</li>
          <li>app and website development providers;</li>
          <li>analytics and diagnostics providers;</li>
          <li>customer support tools and communication tools;</li>
          <li>email and CRM providers;</li>
          <li>legal, compliance, insurance and professional advisers;</li>
          <li>fraud prevention and security providers;</li>
          <li>
            Apple App Store and Google Play Store, where relevant to subscriptions and in-app
            purchases;
          </li>
          <li>service providers who help us operate, maintain, secure or improve NutriCoach;</li>
          <li>
            courts, regulators, law enforcement agencies or other authorities where required by law;
          </li>
          <li>
            a buyer, investor, successor or transaction counterparty if all or part of our business
            is sold, merged, reorganised or transferred.
          </li>
        </ul>
        <h3 className="font-semibold">10.1 AI coaching and voice processing providers</h3>
        <p>To provide the features you request, we currently share relevant personal data with:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>OpenAI</strong>, which processes messages, submitted photographs, relevant
            meal-plan, nutrition, profile, and health-related context, and technical session
            information that does not directly identify your NutriCoach account to generate and
            deliver AI-powered coaching responses;
          </li>
          <li>
            <strong>Google (Gemini)</strong>, which processes messages, submitted photographs,
            relevant meal-plan, nutrition, profile, and health-related context, and technical
            session information that does not directly identify your NutriCoach account to generate
            and deliver AI-powered coaching responses; and
          </li>
          <li>
            <strong>Groq</strong>, which processes voice recordings and associated technical
            information to produce text transcriptions when you use voice input.
          </li>
        </ul>
        <p>
          We share only the information reasonably required to provide the feature you have chosen
          to use. We do not intentionally share your name, email address, or NutriCoach user ID with
          these providers. Content you choose to submit may nevertheless identify you or another
          person if you include identifying information in a message, photograph, or voice
          recording. We require these providers to protect personal data in line with applicable law
          and our contractual requirements, and we do not authorise them to use it for their own
          independent marketing.
        </p>
        <p>We do not sell your personal data.</p>
        <p>
          We do not share your personal data with third parties for their own independent marketing
          unless we have clearly told you and have a valid legal basis to do so.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">11. International transfers</h2>
        <p>
          NutriCoach may use service providers located in, or storing data in, countries outside the
          UK.
        </p>
        <p>
          Where we transfer personal data internationally, we will take steps designed to ensure it
          remains protected in line with applicable data protection law, for example by using
          approved contractual safeguards or other lawful transfer mechanisms where required.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">12. How long we keep your personal data</h2>
        <p>
          UK GDPR requires privacy information to explain retention periods, or at least the
          criteria used to decide them.
        </p>
        <p>
          We keep personal data only for as long as reasonably necessary for the purposes set out in
          this Privacy Policy, including to provide the Platform, comply with legal obligations,
          resolve disputes, enforce agreements and maintain business records.
        </p>
        <p>In practice:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            account and profile data is usually kept while your account is active and for a
            reasonable period afterwards;
          </li>
          <li>
            support communications may be kept for record-keeping and dispute-handling purposes;
          </li>
          <li>
            transaction and subscription records may be kept for tax, audit and accounting purposes;
          </li>
          <li>
            analytics data may be kept in aggregated or de-identified form for longer where lawful.
          </li>
          <li>
            AI messages, submitted photographs, voice recordings, transcriptions, and related
            non-identifying session information are kept only for as long as reasonably necessary to
            provide the requested feature, maintain conversation continuity, secure and troubleshoot
            the service, and meet applicable legal obligations. Our providers may process or retain
            this information on our behalf in accordance with our contracts and their applicable
            service terms.
          </li>
        </ul>
        <p>
          If you request deletion, we will assess that request in line with applicable law. We may
          keep limited data where we are required or entitled to do so, such as for legal,
          fraud-prevention, accounting or record-keeping purposes.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">13. Your rights</h2>
        <p>Depending on where you are and subject to applicable law, you may have the right to:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>access your personal data;</li>
          <li>request correction of inaccurate personal data;</li>
          <li>request deletion of your personal data;</li>
          <li>request restriction of processing;</li>
          <li>object to certain processing;</li>
          <li>withdraw consent where we rely on consent;</li>
          <li>request transfer of certain data to you or another provider;</li>
          <li>complain to a supervisory authority.</li>
        </ul>
        <p>
          ICO guidance states privacy notices should tell people which rights they have and how to
          complain.
        </p>
        <p>
          To exercise your rights, contact us at <strong>hello@nutricoach.co.uk</strong>. We may
          need to verify your identity before acting on your request.
        </p>
        <p>
          You may also withdraw permission for future sharing with our AI coaching and voice
          processing providers by contacting us at that address. If you withdraw permission, we will
          stop sending new information to those providers, but AI coaching, voice transcription, and
          related features may no longer be available to you.
        </p>
        <p>
          If you are in the UK, you also have the right to complain to the{" "}
          <strong>Information Commissioner&apos;s Office (ICO)</strong>.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">14. Marketing communications</h2>
        <p>
          We may send you service-related communications that are necessary for your account or
          subscription.
        </p>
        <p>
          Where permitted by law, we may also send marketing communications about NutriCoach,
          updates, offers or related content. You can opt out of marketing at any time by using the
          unsubscribe link or contacting us.
        </p>
        <p>
          Opting out of marketing does not stop service-related messages, such as subscription,
          account or security communications.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">15. Children</h2>
        <p>
          NutriCoach is not intended for anyone under 18. We do not knowingly collect personal data
          from children under 18 for use of the Platform. If you believe a child has provided us
          with personal data, please contact us and we will investigate.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">16. Security</h2>
        <p>
          We use measures designed to protect personal data against accidental or unlawful
          destruction, loss, alteration, unauthorised disclosure or access.
        </p>
        <p>
          However, no platform, transmission method or storage system is completely secure, and we
          cannot guarantee absolute security.
        </p>
        <p>
          If we become aware of a personal data breach, we will respond in accordance with
          applicable law. UK law requires notification in certain cases.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">17. Third-party links and services</h2>
        <p>
          The Platform may contain links to third-party websites, services or tools. We are not
          responsible for the privacy practices of third parties. You should read their privacy
          notices before submitting personal data to them.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">18. Changes to this Privacy Policy</h2>
        <p>We may update this Privacy Policy from time to time.</p>
        <p>
          Where changes are material, we will take reasonable steps to notify you, for example by
          email, in-app notice or website notice. The latest version will always be the version made
          available through our Platform.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">19. Contact us</h2>
        <p>
          If you have questions about this Privacy Policy or how we handle your personal data,
          contact:
        </p>
        <address className="space-y-1 not-italic">
          <p>
            <strong>NUTRICOACH LIMITED</strong>
          </p>
          <p>79 The Mount, Hale Barns, Cheshire, WA15 8SZ, United Kingdom</p>
          <p>
            <strong>Email:</strong> hello@nutricoach.co.uk
          </p>
        </address>
        <p>
          If you are unhappy with our response, you may have the right to complain to the
          Information Commissioner&apos;s Office.
        </p>
      </section>
    </div>
  );
};
